import Link from "next/link";
import InquiryForm from "./inquiry-form";
import SecondaryButton from "@/components/ui/secondary-button";
import Image from "next/image";
import ContactImage from "@/assets/images/home/contact-image.jpeg";
import { CALLRAIL_HREF, CALLRAIL_PRETTY, withUrl } from "@/lib/utils";
import LINKS from "@/lib/links";

type CTA = {
  heading: string;
  subheading: string;
};

export interface ContactProps {
  id?: string;
  cta1?: CTA;
  cta2?: CTA;
}

const defaultProps: ContactProps = {
  cta1: {
    heading: "Have more questions?",
    subheading: "You can always ask us, we're here to help you.",
  },
  cta2: {
    heading: "Interested to work with us?",
    subheading: "Check our Careers page to view open positions.",
  },
};

const ContactHome: React.FC<ContactProps> = ({
  id = "contact",
  cta1,
  cta2,
}) => {
  // Set default props if they are not defined
  cta1 = cta1 ?? defaultProps.cta1;
  cta2 = cta2 ?? defaultProps.cta2;

  return (
    <section
      id={id}
      aria-label={id}
      className="bg-primary-100"
    >
      <div className="mx-auto grid max-w-lg grid-cols-2 gap-6 p-4 md:px-6 md:py-8 lg:px-3 lg:py-10">
        <div className="col-span-2 flex flex-col lg:col-span-1 lg:basis-1/2 lg:gap-4">
          <div className="grid grid-cols-2 gap-6 rounded-3xl bg-primary-100 px-4 lg:gap-8 lg:px-0 lg:py-8">
            <div className="col-span-2 flex flex-col items-center gap-2 md:col-span-1 md:gap-4 lg:col-span-2 lg:items-start">
              <div className="flex flex-col items-center gap-1 text-center md:gap-2 lg:items-start lg:justify-start">
                <h3 className="font-montserrat text-subheading-3-500 text-neutral-600 md:text-subheading-2 lg:text-subheading-1">
                  {cta1?.heading}
                </h3>
                <p className="font-montserrat text-subheading-3-400 text-neutral-500">
                  {cta1?.subheading}
                </p>
              </div>
              <SecondaryButton
                id="primary-cta-home-hotline"
                className="lg:mr-auto lg:block"
                color="gray"
                size="md"
                asChild
              >
                <a href={CALLRAIL_HREF}>Call Us at {CALLRAIL_PRETTY}</a>
              </SecondaryButton>
            </div>
            <div className="col-span-2 flex flex-col items-center gap-2 md:col-span-1 md:gap-4 lg:col-span-2 lg:items-start">
              <div className="flex flex-col items-center gap-1 text-center md:gap-2 lg:items-start lg:justify-start">
                <h3 className="whitespace-nowrap font-montserrat text-subheading-3-500 text-neutral-600 md:text-subheading-2 lg:text-subheading-1">
                  {cta2?.heading}
                </h3>
                <p className="font-montserrat text-subheading-3-400 text-neutral-500">
                  {cta2?.subheading}
                </p>
              </div>
              <SecondaryButton
                className="lg:mr-auto lg:block"
                color="gray"
                size="md"
                asChild
              >
                <Link
                  href={withUrl(LINKS.careers.href)}
                  prefetch
                >
                  Discover Job Opportunities
                </Link>
              </SecondaryButton>
            </div>
          </div>
          <div className="hidden h-[312px] overflow-hidden rounded-3xl shadow-[-8px_8px_0px_0px_#FFD5BF] lg:flex">
            <Image
              className="object-cover"
              src={ContactImage}
              alt="Contact Banner"
            />
          </div>
        </div>
        <InquiryForm className="col-span-2 shadow-[2px_4px_16px_2px_rgba(82,77,75,0.12)] md:shadow-[4px_6px_20px_2px_rgba(82,77,75,0.12)] lg:col-span-1 lg:shadow-[4px_8px_24px_2px_rgba(82,77,75,0.1)]" />
      </div>
    </section>
  );
};

export default ContactHome;
